import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(name, email, password, 'student');
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex" data-testid="register-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-[#002FA7] rounded-sm flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black tracking-tight text-[#0A0A0A]">RIKIRTUTOR</span>
            </Link>
            <h1 className="text-3xl font-bold text-[#0A0A0A] mb-2">Create your account</h1>
            <p className="text-[#4B5563]">Start your learning journey today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-[#E53935]/10 border border-[#E53935]/20 text-[#E53935] px-4 py-3 rounded-sm text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="name" className="text-[#0A0A0A] font-medium">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="mt-2 rounded-sm border-[#E5E7EB] focus:ring-[#002FA7]"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 rounded-sm border-[#E5E7EB] focus:ring-[#002FA7]"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0A0A0A] font-medium">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="rounded-sm border-[#E5E7EB] focus:ring-[#002FA7] pr-10"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#002FA7] hover:bg-[#001B63] text-white font-bold py-6 rounded-sm"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#4B5563]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="login-link">
              Log in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex lg:flex-1 bg-[#002FA7] items-center justify-center p-12">
        <div className="max-w-md">
          <h2 className="text-3xl font-bold text-white mb-8">Why Choose RikirTutor?</h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-white mb-1">Expert Tutors</h3>
                <p className="text-white/70 text-sm">Learn from experienced educators with proven track records.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-white mb-1">Flexible Scheduling</h3>
                <p className="text-white/70 text-sm">Book sessions that fit your schedule, any day of the week.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-white mb-1">AI Study Assistant</h3>
                <p className="text-white/70 text-sm">Get 24/7 help with our intelligent learning assistant.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-white mb-1">Live Video Sessions</h3>
                <p className="text-white/70 text-sm">Connect face-to-face with your tutor from anywhere.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
